import * as React from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  GridToolbar,
} from "@mui/x-data-grid";
import { ptBR } from "@mui/x-data-grid/locales";
import {
  VisibilityOutlined,
  Edit,
  Delete,
  Check,
  Block,
} from "@mui/icons-material";
import { Cobranca, Matricula } from "../../mock/AlunosMock";

export type CustomRowBase = {
  id: string | number;
  idMatricula?: string | number;
  nome?: string;
  email?: string;
  cep?: string;
  logradouro?: string;
  numero?: string;
  bairro?: string;
  cidadeIBGE?: string;
  telCelular?: string;
  sexo?: string;
  nascimento?: string;
  ativo?: boolean;
  matricula?: Matricula; // 👈 agora tem matrícula
  descricao?: string;
  cobrancas?: Cobranca[];
  inativo?: boolean;
  valorPago?: number;
  totalGeral?: number;
  idSituacao?: string;
} & Record<string, unknown>;

export type CustomAction = "view" | "edit" | "delete" | "enable" | "disable";
type PaginationModel = { page: number; pageSize: number };

export interface CustomDatagridProps<T extends CustomRowBase = CustomRowBase> {
  rows?: T[];
  extraColumns?: Array<GridColDef<T>>;
  visibleColumns?: string[]; // lista de fields em ordem
  actions?: CustomAction[];
  onView?: (row: T) => void;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  onEnable?: (row: T) => void;
  onDisable?: (row: T) => void;
  rowHeight?: number;
  notShowFilters?: boolean;
  getRowId?: (row: T) => GridRowId;
  loading?: boolean;
  paginationMode?: "client" | "server";
  paginationModel?: PaginationModel;
  onPaginationModelChange?: (model: PaginationModel) => void;
  rowCount?: number; // total de linhas (server-side): totalPages * 30
}

export function CustomDatagrid<T extends CustomRowBase = CustomRowBase>({
  rows = [] as T[],
  extraColumns = [],
  visibleColumns,
  actions = ["view", "edit", "delete"],
  onView = (r) => console.log("view:", r),
  onEdit = (r) => console.log("edit:", r),
  onDelete = (r) => console.log("delete:", r),
  onEnable = (r) => console.log("enable : ", r),
  onDisable = (r) => console.log("disable : ", r),
  rowHeight = 64,
  notShowFilters = true,
  getRowId,
  loading = false,
  paginationMode = "client",
  paginationModel = { page: 0, pageSize: 30 },
  onPaginationModelChange,
  rowCount,
}: CustomDatagridProps<T>) {
  const formatarData = (data: string): string => {
    if (!data) return "";

    try {
      const [ano, mes, dia] = data.split("-");
      if (ano && mes && dia) {
        return `${dia}/${mes}/${ano}`;
      }
      return data;
    } catch {
      return data;
    }
  };

  const formatarSexo = (sexo: string | number): string => {
    if (sexo === "M") return "Masculino";
    if (sexo === "F") return "Feminino";
    return String(sexo || "");
  };

  const actionButtons = React.useMemo(
    () =>
      [
        {
          id: "view",
          title: "Ver",
          icon: <VisibilityOutlined fontSize="small" />,
          onClick: onView,
          color: "inherit" as const,
        },
        {
          id: "edit",
          title: "Editar",
          icon: <Edit fontSize="small" />,
          onClick: onEdit,
          color: "primary" as const,
        },
        {
          id: "enable",
          title: "Ativar",
          icon: (
            <Box
              sx={{
                px: 0.55,
                py: 0.55,
                bgcolor: "#004AAD",
                borderRadius: 0.75,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 0,
              }}
            >
              <Check fontSize="small" sx={{ color: "#fff" }} />
            </Box>
          ),
          onClick: onEnable,
        },
        {
          id: "disable",
          title: "Desativar",
          icon: (
            <Box
              sx={{
                px: 0.55,
                py: 0.55,
                bgcolor: "#FFC107",
                borderRadius: 0.75,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 0,
              }}
            >
              <Block fontSize="small" sx={{ color: "#000" }} />
            </Box>
          ),
          onClick: onDisable,
          color: "error" as const,
        },
        {
          id: "delete",
          title: "Excluir",
          icon: <Delete fontSize="small" />,
          onClick: onDelete,
          color: "error" as const,
        },
      ].filter((a) => actions.indexOf(a.id as CustomAction) !== -1),
    [actions, onView, onEdit, onDelete, onEnable, onDisable]
  );

  const shouldShowActions = React.useMemo(
    () =>
      (actions?.length ?? 0) > 0 &&
      (!visibleColumns || visibleColumns.includes("actions")),
    [actions, visibleColumns]
  );

  const baseColumns = React.useMemo<GridColDef<T>[]>(() => {
    const idMatricula: GridColDef<T> = {
      field: "idMatricula",
      headerName: "ID Matrícula",
      minWidth: 20,
      flex: 0.5,
      align: "center",
      headerAlign: "center",
    };
    const id: GridColDef<T> = {
      field: "id",
      headerName: "ID",
      minWidth: 20,
      flex: 0.5,
      align: "center",
      headerAlign: "center",
    };
    const email: GridColDef<T> = {
      field: "email",
      headerName: "E-mail",
      flex: 1,
      minWidth: 220,
    };
    const nome: GridColDef<T> = {
      field: "nome",
      headerName: "Nome",
      flex: 1,
      minWidth: 210,
    };
    const telCelular: GridColDef<T> = {
      field: "telCelular",
      headerName: "Telefone Celular",
      flex: 1,
      minWidth: 150,
    };
    const sexo: GridColDef<T> = {
      field: "sexo",
      headerName: "Sexo",
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<T>) => {
        return formatarSexo(params.value);
      },
    };
    const nascimento: GridColDef<T> = {
      field: "nascimento",
      headerName: "Nascimento",
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<T>) => {
        return formatarData(params.value);
      },
    };
    const cep: GridColDef<T> = {
      field: "cep",
      headerName: "CEP",
      flex: 1,
      minWidth: 150,
    };
    const logradouro: GridColDef<T> = {
      field: "logradouro",
      headerName: "Logradouro",
      flex: 1,
      minWidth: 150,
    };
    const numero: GridColDef<T> = {
      field: "numero",
      headerName: "Número",
      flex: 1,
      minWidth: 100,
    };
    const bairro: GridColDef<T> = {
      field: "bairro",
      headerName: "Bairro",
      flex: 1,
      minWidth: 150,
    };
    const cidadeIBGE: GridColDef<T> = {
      field: "cidadeIBGE",
      headerName: "Cidade IBGE",
      flex: 1,
      minWidth: 110,
    };
    const descricao: GridColDef<T> = {
      field: "descricao",
      headerName: "Descrição",
      flex: 1,
      minWidth: 370,
    };
    const valorPago: GridColDef<T> = {
      field: "valorPago",
      headerName: "Valor Pago",
      flex: 1,
      minWidth: 370,
    };
    const totalGeral: GridColDef<T> = {
      field: "totalGeral",
      headerName: "Total Geral",
      flex: 1,
      minWidth: 370,
    };
    const actionsCol: GridColDef<T> = {
      field: "actions",
      headerName: "Ações",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      width: 150,
      renderCell: (params) => {
        const isActive = !!(params.row as any).ativo;
        const visibleButtons = actionButtons.filter((a) => {
          if (a.id === "enable") return !isActive;
          if (a.id === "disable") return isActive;
          return true;
        });
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            {visibleButtons.map((a) => (
              <Tooltip key={a.title} arrow title={a.title}>
                <span>
                  <IconButton
                    size="small"
                    color={a.color}
                    onClick={() => a.onClick?.(params.row)}
                    disabled={!a.onClick}
                  >
                    {a.icon}
                  </IconButton>
                </span>
              </Tooltip>
            ))}
          </Box>
        );
      },
    };

    const cols: GridColDef<T>[] = [
      idMatricula,
      id,
      nome,
      email,
      telCelular,
      sexo,
      nascimento,
      cep,
      logradouro,
      numero,
      bairro,
      cidadeIBGE,
      descricao,
      valorPago,
      totalGeral,
    ];
    if (shouldShowActions) cols.push(actionsCol); // <-- só adiciona se precisar
    return cols;
  }, [actionButtons, shouldShowActions]);

  const allCols = React.useMemo<GridColDef<T>[]>(() => {
    const indexByField: { [field: string]: number } = {};
    const merged: GridColDef<T>[] = [];

    for (let i = 0; i < baseColumns.length; i++) {
      const c = baseColumns[i];
      if (c && c.field) {
        indexByField[c.field] = merged.length;
        merged.push(c);
      }
    }
    for (let i = 0; i < extraColumns.length; i++) {
      const c = extraColumns[i];
      if (!c || !c.field) continue;
      const idx = indexByField[c.field];
      if (typeof idx === "number") {
        merged[idx] = c; // override
      } else {
        indexByField[c.field] = merged.length;
        merged.push(c);
      }
    }
    return merged;
  }, [baseColumns, extraColumns]);

  const columns = React.useMemo<GridColDef<T>[]>(() => {
    const findByField = (field: string): GridColDef<T> | undefined => {
      for (let i = 0; i < allCols.length; i++) {
        if (allCols[i]?.field === field) return allCols[i];
      }
      return undefined;
    };

    if (visibleColumns && visibleColumns.length) {
      const picked: GridColDef<T>[] = [];
      for (let i = 0; i < visibleColumns.length; i++) {
        const field = visibleColumns[i];
        const col = findByField(field);
        if (col && col.field !== "actions") picked.push(col);
      }
      const actionsCol = findByField("actions");
      if (actionsCol) picked.push(actionsCol);
      return picked;
    }

    const nonActions: GridColDef<T>[] = [];
    let actionsCol: GridColDef<T> | undefined;
    for (let i = 0; i < allCols.length; i++) {
      const c = allCols[i];
      if (!c) continue;
      if (c.field === "actions") actionsCol = c;
      else nonActions.push(c);
    }
    if (actionsCol) nonActions.push(actionsCol);
    return nonActions;
  }, [allCols, visibleColumns]);

  const rowId = React.useCallback(
    (r: T) => (getRowId ? getRowId(r) : r.id),
    [getRowId]
  );

  return (
    <>
      {window.screen.width <= 768 ? (
        <div
          style={{
            overflowX: "auto",
            display: "flex",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <DataGrid
            // hideFooter // TIRAR O HIDEFOOTER CASO QUEIRA A PAGINAÇÃO E TAMBÉM ROWS PER PAGE
            rows={rows}
            columns={columns}
            getRowId={rowId}
            rowHeight={rowHeight}
            autoHeight
            density="compact"
            disableRowSelectionOnClick
            loading={loading}
            pagination
            paginationMode={paginationMode ?? "client"}
            rowCount={rowCount ?? rows.length}
            paginationModel={paginationModel ?? { page: 0, pageSize: 30 }}
            onPaginationModelChange={(model) => {
              const fixed = { page: model.page, pageSize: 30 };
              onPaginationModelChange?.(fixed);
            }}
            pageSizeOptions={[30]}
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            slots={{ toolbar: notShowFilters ? undefined : GridToolbar }}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#004AAD",
                "& .MuiDataGrid-columnHeader": {
                  borderRight: "1px solid #e0e0e0",
                  "&:last-of-type": { borderRight: "none" },
                },
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#004AAD",
                color: "white",
                fontSize: "14px",
                fontWeight: "bold",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "white",
                fontWeight: "bold",
              },
              "& .MuiDataGrid-cell": {
                borderRight: "1px solid #e0e0e0",
                "&:last-of-type": { borderRight: "none" },
              },
              "& .MuiDataGrid-row.even .MuiDataGrid-cell": {
                backgroundColor: "#F1F1F1",
              },
              "& .MuiDataGrid-row.odd .MuiDataGrid-cell": {
                backgroundColor: "#fff",
              },
              "& .MuiDataGrid-row:hover .MuiDataGrid-cell": {
                backgroundColor: "#DFEAF6",
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                { outline: "none" },
              border: "1px solid #e0e0e0",
              borderRadius: "4px",
            }}
          />
        </div>
      ) : (
        <DataGrid
          // hideFooter
          rows={rows}
          columns={columns}
          getRowId={rowId}
          rowHeight={rowHeight}
          autoHeight
          density="compact"
          disableRowSelectionOnClick
          loading={loading}
          pagination
          paginationMode={paginationMode ?? "client"}
          rowCount={rowCount ?? rows.length}
          paginationModel={paginationModel ?? { page: 0, pageSize: 30 }}
          onPaginationModelChange={(model) => {
            const fixed = { page: model.page, pageSize: 30 };
            onPaginationModelChange?.(fixed);
          }}
          pageSizeOptions={[30]} // fixa em 30
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
          slots={{ toolbar: notShowFilters ? undefined : GridToolbar }}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
          }
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#004AAD",
              "& .MuiDataGrid-columnHeader": {
                borderRight: "1px solid #e0e0e0",
                "&:last-of-type": { borderRight: "none" },
              },
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#004AAD",
              color: "white",
              fontSize: "14px",
              fontWeight: "bold",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "white",
              fontWeight: "bold",
            },
            "& .MuiDataGrid-cell": {
              borderRight: "1px solid #e0e0e0",
              "&:last-of-type": { borderRight: "none" },
            },
            "& .MuiDataGrid-row.even .MuiDataGrid-cell": {
              backgroundColor: "#F1F1F1",
            },
            "& .MuiDataGrid-row.odd .MuiDataGrid-cell": {
              backgroundColor: "#fff",
            },
            "& .MuiDataGrid-row:hover .MuiDataGrid-cell": {
              backgroundColor: "#DFEAF6",
            },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
              { outline: "none" },
            border: "1px solid #e0e0e0",
            borderRadius: "4px",
          }}
        />
      )}
    </>
  );
}
